//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  deleteCollection,
  updateSavedCollection,
  updateSavedCollectionName,
} from '@apis/collections/collectionsApi';
import {
  CollectionUpdateNameRequest,
  CollectionUpdateRequest,
  DeleteCollectionRequest,
} from '@apis/collections/collectionsApiModel';
import {
  getContentLibrarySettings,
  updateContentLibrarySettings,
} from '@apis/content-library/contentLibraryApi';
import { ContentLibrarySettingsRequest } from '@apis/content-library/contentLibraryApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { queryClient } from '@queries/ReactQueryProvider';
import { navigateToState } from '@react-bridge/wrappers/navigation';

export function useUpdateCollectionNameMutation() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ collectionId, name }: CollectionUpdateNameRequest) => {
      return updateSavedCollectionName({ collectionId, name });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaLibrary,
          QueryKeyConstants.MediaLibraryCollections,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Changes Saved',
          message: 'Your collection has been successfully renamed.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error Changing Collection Name',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export const useGetContentLibrarySettingsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryCollections,
      QueryKeyConstants.MediaLibrarySettings,
    ],
    queryFn: async ({ signal }) => {
      return getContentLibrarySettings({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    keepPreviousData: true,
  });
};

export function useUpdateContentLibrarySettingsMutation() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (req: ContentLibrarySettingsRequest) => {
      return updateContentLibrarySettings(req);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaLibrary,
          QueryKeyConstants.MediaLibraryCollections,
          QueryKeyConstants.MediaLibrarySettings,
        ],
      });
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Something Went Wrong',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}

export function useDeleteCollectiontMutation() {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ collectionId }: DeleteCollectionRequest) => {
      await deleteCollection({ collectionId });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaLibrary,
          QueryKeyConstants.MediaLibraryCollections,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Success',
          message: 'Collection deleted successfully',
          type: ToastType.SUCCESS,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );

      void navigateToState('library.all-media');
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Failed',
          message: 'An error occurred while deleting the collection.',
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
  });
}

export function useUpdateCollectionMutation() {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (req: CollectionUpdateRequest) => {
      return updateSavedCollection(req);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.MediaLibrary,
          QueryKeyConstants.MediaLibraryCollections,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Changes Saved',
          message: 'Your collection has been successfully updated.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error Updating Collection',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
}
